import request from "@/services/request";
import EchoManager from "@/services/echo-manager";
import PushManager from "@/services/push-manager";
import IapManager from "@/services/iap-manager";
import AdManager from "@/services/adManager";
import StorageManager from "@/services/storageManager";
import ImageCacheManager from "@/services/imageCacheManager";
import EventBus from "@/services/eventBus";
import {cleanupStaleNotifications} from "@/services/notificationCleanup";
import {RewardType, GuildRank, CurrencyType} from "@/enums";
import {init} from "./init";

// Mirrors the backend DivisionRewardService::FLOOR_OFFSET. Rewards for a
// difficulty are eligible only within [max(1, peak - DIVISION_REWARD_FLOOR_OFFSET), peak].
const DIVISION_REWARD_FLOOR_OFFSET = 4;

// Recompute the eligible daily-reward band for a division given its current
// peak difficulty, preserving any difficulties already claimed today. Difficulties
// that fall below the new floor are dropped from both lists.
function buildDailyRewardBand(peak, existing = null) {
    const floor = Math.max(1, peak - DIVISION_REWARD_FLOOR_OFFSET);
    const claimed = (existing && Array.isArray(existing.claimed) ? existing.claimed : [])
        .filter(difficulty => difficulty >= floor && difficulty <= peak);
    const unclaimed = [];
    for (let difficulty = floor; difficulty <= peak; difficulty++) {
        if (! claimed.includes(difficulty)) {
            unclaimed.push(difficulty);
        }
    }
    return {
        date: existing && existing.date ? existing.date : null,
        floor,
        peak,
        unclaimed,
        claimed,
    };
}

export const actions = {
    RESET() {
        Object.keys(init).forEach(key => {
            this[key] = init[key];
        });
        Object.keys(this.$state).forEach(key => {
            if (! Object.hasOwnProperty.call(init, key)) {
                delete this.$state[key];
            }
        });
    },
    RESET_PROPERTY(property) {
        if (Object.hasOwnProperty.call(init, property)) {
            if (Array.isArray(init[property])) {
                this[property] = [];
            } else {
                this[property] = init[property];
            }
        }
    },
    SET_BAN(ban) {
        this.ban = ban;
        if (ban !== null && window.echo && window.echo.state === 'connected') {
            EchoManager.unsubscribeChannel('bracket');
            EchoManager.unsubscribeChannel('guild');
            EchoManager.unsubscribeChannel('user');
            EchoManager.unsubscribeChannel('system');
        }
    },
    SET_MAINTENANCE(maintenance) {
        this.maintenance = maintenance;
        if (maintenance !== null && window.echo && window.echo.state === 'connected') {
            EchoManager.unsubscribeChannel('bracket');
            EchoManager.unsubscribeChannel('guild');
            EchoManager.unsubscribeChannel('user');
            EchoManager.unsubscribeChannel('system');
        }
    },
    SET_REFERENCES_ID(id) {
        this.references.id = id;
    },
    SET_REFERENCES_DATA(data) {
        this.references.data = data;
    },
    SET_REFERENCES_LOADING(loading) {
        this.references.loading = loading;
    },
    SET_REFERENCES_LOADED(loaded) {
        this.references.loaded = loaded;
    },
    SET_REFERENCES_ERROR(error) {
        this.references.error = error;
    },
    SET_ECHO_CONNECTING() {
        this.echo.state = 'connecting';
    },
    SET_ECHO_CONNECTED(socket) {
        this.echo = {state: 'connected', socket};
    },
    SET_ID(id) {
        this.id = id;
    },
    SET_TOKEN(token) {
        this.token = token;
    },
    SET_SESSION(session) {
        this.session = session;
    },
    SET_STREAK(streak) {
        this.streak = streak;
    },
    SET_USERNAME(username) {
        this.username = username;
    },
    SET_COPPA(coppa) {
        this.coppa = coppa;
    },
    SET_TUTORIAL_COMPLETED(tutorial_completed) {
        this.tutorial_completed = tutorial_completed;
    },
    SET_PROFANITY(profanity) {
        this.profanity = profanity;
    },
    SET_UNCLAIMED_VIP(unclaimed_vip) {
        this.unclaimed_vip = unclaimed_vip;
    },
    SET_SEASON(season) {
        this.season = season;
    },
    SET_SALES(sales) {
        this.sales = sales;
        if (this.references.id) {
            ImageCacheManager.cacheSaleImages(this.references.id, sales);
        }
    },
    UPDATE_SALE(sale) {
        this.sales = this.sales.filter(s => {
            return s.id !== sale.id;
        });
        this.sales.push(sale);
    },
    DELETE_SALE(sale_id) {
        this.sales = this.sales.filter(s => {
            return s.id !== sale_id;
        });
    },
    SET_LIMITED_CHESTS(chests) {
        this.limited_chests = chests;
    },
    // TODO: THIS SHOULD BE 'INCREMENT_LIMITED_CHEST'
    UPDATE_LIMITED_CHEST(chest_id) {
        const found = this.limited_chests.find(c => c.id === chest_id);
        if (found) {
            found.amount++;
        } else {
            this.limited_chests.push({
                id: chest_id,
                amount: 1,
            });
        }
    },
    SET_LIMITED_DISCOUNTS(discounts) {
        this.limited_discounts = discounts;
    },
    // TODO: THIS SHOULD BE `INCREMENT_LIMITED_DISCOUNT'
    UPDATE_LIMITED_DISCOUNT(discount_id) {
        const found = this.limited_discounts.find(d => d.id === discount_id);
        if (found) {
            found.amount++;
        } else {
            this.limited_discounts.push({
                id: discount_id,
                amount: 1,
            });
        }
    },
    ADD_CHARACTER_DIVISION(payload) {
        const difficulty = this.references.data.design.division.find(d => d.id === payload.division).prerequisite;
        const character = this.characters.find(c => c.id === payload.character);
        const found = character.divisions.find(d => d.id === payload.division);
        if (! found) {
            character.divisions.push({
                id: payload.division,
                difficulty,
                daily_rewards: buildDailyRewardBand(difficulty),
            });
        }
    },
    INCREMENT_CHARACTER_DIVISION_DIFFICULTY(payload) {
        const found = this.characters.find(c => c.id === payload.character).divisions.find(d => d.id === payload.division);
        if (found) {
            found.difficulty++;
            // Slide the eligible daily-reward band up to match the new peak,
            // preserving difficulties already claimed today.
            found.daily_rewards = buildDailyRewardBand(found.difficulty, found.daily_rewards);
        }
    },
    CLAIM_DIVISION_DAILY_REWARD(payload) {
        const found = this.characters.find(c => c.id === payload.character)?.divisions.find(d => d.id === payload.division);
        if (! found || ! found.daily_rewards) {
            return;
        }
        const difficulty = payload.difficulty;
        if (found.daily_rewards.unclaimed.includes(difficulty)) {
            found.daily_rewards.unclaimed = found.daily_rewards.unclaimed.filter(d => d !== difficulty);
        }
        if (! found.daily_rewards.claimed.includes(difficulty)) {
            found.daily_rewards.claimed = [...found.daily_rewards.claimed, difficulty].sort((a, b) => a - b);
        }
    },
    SET_CHARACTERS(characters) {
        this.characters = characters;
    },
    SET_ABILITY_POINTS(ability_points) {
        this.ability_points = ability_points;
    },
    UPDATE_ABILITY_POINTS(payload) {
        const found = this.ability_points.find(p => p.id === payload.id);
        if (found) {
            found.quantity += payload.change;
            if (found.quantity < 1) {
                this.ability_points = this.ability_points.filter(p => {
                    return p.id !== payload.id;
                });
            }
        } else {
            this.ability_points.push({
                id: payload.id,
                quantity: payload.change,
            });
        }
    },
    SET_CHARACTER_POINTS(character_points) {
        this.character_points = character_points;
    },
    UPDATE_CHARACTER_POINTS(payload) {
        const found = this.character_points.find(c => c.id === payload.id);
        if (found) {
            found.quantity += payload.change;
            if (found.quantity < 1) {
                this.character_points = this.character_points.filter(p => {
                    return p.id !== payload.id;
                });
            }
        } else {
            if (payload.change > 0) {
                this.character_points.push({
                    id: payload.id,
                    quantity: payload.change,
                });
            }
        }
    },
    SET_ATTRIBUTE_POINTS(attribute_points) {
        this.currencies.attribute_points = attribute_points;
    },
    UPDATE_ATTRIBUTE_POINTS(amount) {
        this.currencies.attribute_points += amount;
    },
    ADD_CHARACTER_TREE(payload) {
        const character = this.characters.find(c => c.id === payload.character_id);
        if (character) {
            const first = character.character_trees[0];
            character.character_trees.push({
                id: payload.id,
                active: true,
                attributes: first.attributes.map(a => {
                    return {
                        id: a.id,
                        val: 0,
                    }
                }),
            });
            character.character_trees.forEach(t => {
                if (t.id !== payload.id) {
                    t.active = false;
                }
            });
        }
    },
    ADD_CHARACTER_LOADOUT(payload) {
        const character = this.characters.find(c => c.id === payload.character_id);
        if (character) {
            const first = character.character_loadouts[0];
            character.character_loadouts.push({
                id: payload.id,
                active: true,
                slots: first.slots.map(s => {
                    return {
                        slot: s.slot,
                        ability_id: null,
                        tier: null,
                    }
                }),
            });
            character.character_loadouts.forEach(l => {
                if (l.id !== payload.id) {
                    l.active = false;
                }
            });
        }
    },
    ACTIVATE_CHARACTER_LOADOUT(payload) {
        const character = this.characters.find(c => c.id === payload.character_id);
        if (character) {
            character.character_loadouts.forEach(l => {
                l.active = l.id === payload.loadout_id;
            });
        }
    },
    ACTIVATE_CHARACTER_TREE(payload) {
        const character = this.characters.find(c => c.id === payload.character_id);
        if (character) {
            character.character_trees.forEach(t => {
                t.active = t.id === payload.tree_id;
            });
        }
    },
    SET_CHARACTER_MMR(payload) {
        const character = this.characters.find(c => c.id === payload.character_id);
        if (character) {
            character.mmr = payload.mmr;
        }
    },
    RESET_CHARACTER_TREE(payload) {
        const character = this.characters.find(c => c.id === payload.character_id);
        if (character) {
            const tree = character.character_trees.find(t => t.id === payload.tree_id);
            if (tree) {
                const attributes = tree.attributes;
                // The backend refunds the allocated points on reset, so credit
                // them back to the player's attribute point balance here.
                const refunded = attributes.reduce((sum, a) => sum + a.val, 0);
                attributes.forEach(a => {
                    a.val = 0;
                });
                this.currencies.attribute_points += refunded;
                // THIS IS TO FORCE VUEX TO UPDATE ON THE characters GETTER
                tree.attributes = null;
                tree.attributes = attributes;
            }
        }
    },
    INCREMENT_TREE_ATTRIBUTE(payload) {
        const character = this.characters.find(c => c.id === payload.character_id);
        if (character) {
            const tree = character.character_trees.find(t => t.id === payload.tree_id);
            if (tree) {
                const attribute = tree.attributes.find(a => a.id === payload.attribute_id);
                attribute.val += payload.quantity;
            }
        }
    },
    SET_COINS(coins) {
        this.currencies.coins = coins;
    },
    UPDATE_COINS(amount) {
        this.currencies.coins += amount;
    },
    SET_TICKETS(tickets) {
        this.currencies.tickets = tickets;
    },
    UPDATE_TICKETS(amount) {
        this.currencies.tickets += amount;
    },
    SET_APPLICATIONS(applications) {
        this.applications = applications;
    },
    SET_INVITES(invites) {
        this.invites = invites;
    },
    SET_GUILD(guild) {
        // ORDER OF OPERATIONS MATTERS HERE, DON'T REFACTOR
        if (guild === null && window.echo && window.echo.state === 'connected') {
                EchoManager.unsubscribeChannel('guild');
                EchoManager.unsubscribeChannel('bracket');
        }
        this.guild = guild;
        this.guild_online = [];
        if (guild !== null) {
            guild.rewards = guild.rewards == null ? [] : guild.rewards.rewards;
            // Delay subscription slightly to allow backend membership to propagate
            // before the presence channel authorization request is made.
            setTimeout(() => {
                EchoManager.subscribeChannel('guild');
                EchoManager.subscribeChannel('bracket');
            }, 100);
        }
    },
    SET_GUILD_MEMBER_ONLINE(member) {
        if (this.guild_online.indexOf(member) === -1) {
            this.guild_online.push(member);
        }
    },
    SET_GUILD_MEMBER_OFFLINE(member) {
        this.guild_online = this.guild_online.filter(item => {
            return item !== member;
        });
        if (this.guild !== null) {
            const m = this.guild.members.find(u => u.id === member);
            if (m) {
                m.last_online = Math.floor(Date.now() / 1000);
            }
        }
    },
    SET_GUILD_TIER(tier) {
        if (this.guild !== null) {
            this.guild.tier = tier;
        }
    },
    SET_GUILD_BRACKET(bracket) {
        if (this.guild !== null) {
            if (window.echo && window.echo.state === 'connected') {
                EchoManager.unsubscribeChannel('bracket');
            }
            this.guild.bracket = bracket;
            // TODO: THIS CAN CAUSE A RACE CONDITION WHEN SEASON SWITCHES
            EchoManager.subscribeChannel('bracket');
        }
    },
    SET_GUILD_REWARDS(rewards) {
        if (this.guild !== null) {
            this.guild.rewards = rewards;
        }
    },
    RENAME_GUILD(name) {
        if (this.guild !== null) {
            this.guild.name = name;
        }
    },
    // TODO: ON CREATION OF GUILD, SET MOTD TO A PLACEHOLDER MESSAGE.
    SET_GUILD_MOTD(motd) {
        if (this.guild !== null) {
            this.guild.motd = motd;
        }
    },
    INCREMENT_GUILD_VIP_POINTS(points) {
        if (this.guild !== null) {
            this.guild.vip_points += points;
        }
    },
    DECREMENT_GUILD_VIP_POINTS(points) {
        if (this.guild !== null) {
            this.guild.vip_points = Math.max(0, this.guild.vip_points - points);
        }
    },
    ADD_APPLICATION(application) {
        const found = this.applications.find(a => a.guild.id === application.guild.id);
        if (! found) {
            this.applications.push(application);
        }
    },
    REMOVE_APPLICATION(guild_id) {
        this.applications = this.applications.filter(a => {
            return a.guild.id !== guild_id;
        });
    },
    ADD_INVITE(invite) {
        const found = this.invites.find(i => i.guild.id === invite.guild.id);
        if (! found) {
            this.invites.push(invite);
        }
    },
    REMOVE_INVITE(guild_id) {
        this.invites = this.invites.filter(i => {
            return i.guild.id !== guild_id;
        });
    },
    ADD_GUILD_APPLICATION(application) {
        const found = this.guild.applications.find(a => a.user.id === application.user.id);
        if (! found) {
            this.guild.applications.push(application);
        }
    },
    REMOVE_GUILD_APPLICATION(user_id) {
        this.guild.applications = this.guild.applications.filter(a => {
            return a.user.id !== user_id;
        });
    },
    ADD_GUILD_INVITE(invite) {
        const found = this.guild.invites.find(i => i.user.id === invite.user.id);
        if (! found) {
            this.guild.invites.push(invite);
        }
    },
    REMOVE_GUILD_INVITE(user_id) {
        this.guild.invites = this.guild.invites.filter(i => {
            return i.user.id !== user_id;
        });
    },
    ADD_GUILD_MEMBER(member) {
        const found = this.guild.members.find(m => m.id === member.id);
        if (! found) {
            this.guild.members.push(member);
        }
    },
    REMOVE_GUILD_MEMBER(user_id) {
        this.guild.members = this.guild.members.filter(m => {
            return m.id !== user_id;
        });
    },
    PROMOTE_GUILD_MEMBER(user_id) {
        const found = this.guild.members.find(m => m.id === user_id);
        if (found) {
            found.guild_rank = GuildRank.OFFICER;
        }
    },
    DEMOTE_GUILD_MEMBER(user_id) {
        const found = this.guild.members.find(m => m.id === user_id);
        if (found) {
            found.guild_rank = GuildRank.MEMBER;
        }
    },
    SET_GUILD_LEADER(user_id) {
        const newLeader = this.guild.members.find(m => m.id === user_id);
        const oldLeader = this.guild.members.find(m => m.guild_rank === GuildRank.LEADER);
        if (newLeader && oldLeader && newLeader.id !== oldLeader.id) {
            newLeader.guild_rank = GuildRank.LEADER;
            oldLeader.guild_rank = GuildRank.OFFICER;
        }
    },
    SET_GUILD_CHAT_CURSOR(cursor) {
        this.guild.messages.cursor = cursor;
    },
    ADD_GUILD_CHAT_MESSAGE(message) {
        const found = this.guild.messages.data.find(m => m.id === message.id);
        if (! found) {
            this.guild.messages.data.push(message);
        }
    },
    ADD_CHARACTER(payload) {
        const character = this.characters.find(c => c.id === payload.id);
        if (! character) {
            this.characters.push(payload);
        }
    },
    INCREMENT_CHARACTER_TIER(character_id) {
        const found = this.characters.find(c => c.id === character_id);
        if (found) {
            found.tier++;
        }
    },
    ADD_CHARACTER_ABILITY(payload) {
        const character = this.characters.find(c => c.id === payload.character_id);
        if (character) {
            const abilities = character.abilities;
            const ability = abilities.find(a => a.id === payload.ability_id);
            if (! ability) {
                abilities.push({
                    id: payload.ability_id,
                    tier: 1,
                    slot: null,
                });
                // THIS IS TO FORCE VUEX TO UPDATE ON THE characters GETTER
                character.abilities = null;
                character.abilities = abilities;
            }
        }
    },
    INCREMENT_CHARACTER_ABILITY_TIER(payload) {
        const character = this.characters.find(c => c.id === payload.character_id);
        if (character) {
            const abilities = character.abilities;
            const ability = abilities.find(a => a.id === payload.ability_id);
            if (ability) {
                ability.tier++;
            }
            // THIS IS TO FORCE VUEX TO UPDATE ON THE characters GETTER
            character.abilities = null;
            character.abilities = abilities;
        }
    },
    SET_CHARACTER_LOADOUT(payload) {
        const character = this.characters.find(c => c.id === payload.character_id);
        if (character) {
            const loadout = character.character_loadouts.find(l => l.id === payload.loadout_id);
            if (loadout) {
                loadout.slots = payload.slots;
            }
        }
    },
    SET_NOTIFICATION_PREFERENCES(preferences) {
        this.notification_preferences = preferences;
    },
    SET_NOTIFICATIONS(notifications) {
        this.notifications = notifications;
    },
    ADD_NOTIFICATION(notification) {
        this.notifications.push(notification);
        EventBus.emit('notification-added', notification.id);
    },
    MARK_NOTIFICATION_READ(notification_id) {
        const found = this.notifications.find(n => n.id === notification_id);
        if (found) {
            found.read_at = Math.round(new Date().getTime() / 1000);
        }
    },
    MARK_MESSAGE_READ(message_id) {
        if (this.guild !== null) {
            const found = this.guild.messages.data.find(m => m.id === message_id);
            if (found && ! found.read) {
                found.read = true;
            }
        }
    },
    UPDATE_UNREAD_MESSAGES(amount) {
        if (this.guild !== null) {
            this.guild.messages.unread += amount;
            this.guild.messages.unread = Math.max(0, this.guild.messages.unread);
        }
    },
    REMOVE_NOTIFICATION(notification_id) {
        this.notifications = this.notifications.filter(n => {
            return n.id !== notification_id;
        });
    },
    SET_VOLUME(payload) {
        StorageManager.set('volume', JSON.stringify(payload));
        this.volume = payload;
    },
    SET_HAPTIC(haptic) {
        StorageManager.set('haptic', haptic);
        this.haptic = haptic;
    },
    SET_MASQUERADE(isMasquerade) {
        StorageManager.set('isMasquerade', isMasquerade);
        this.isMasquerade = isMasquerade;
    },
    SET_REPLAY_MODE(mode) {
        StorageManager.set('replayMode', mode);
        this.replayMode = mode;
    },
    SET_PASSCODE(passcode) {
        this.passcode = passcode;
    },
    SET_SUBSCRIPTION_ACTIVE(active) {
        this.subscription_active = active;
    },
    SET_SUBSCRIPTION_CANCELLED(cancelled) {
        this.subscription_cancelled = cancelled;
    },
    SET_SUBSCRIPTION_EXPIRES_AT(expires_at) {
        this.subscription_expires_at = expires_at;
    },
    SET_LOGIN_REWARDS(rewards) {
        this.login_rewards = rewards;
    },
    SET_RELEASE_NOTES(release_notes) {
        this.release_notes = release_notes;
    },
    ACTIVATE_USER_CHARACTER(character_id) {
        this.characters.forEach(character => {
            character.active = character.id === character_id;
        });
    },
    RENAME_COMPETITOR(payload) {
        if (this.guild !== null) {
            const competitor = this.guild.bracket.competitors.find(c => c.id === payload.id);
            competitor.name = payload.name;
        }
    },
    INCREMENT_SEASON_POINTS(payload) {
        if (this.guild !== null) {
            const competitor = this.guild.bracket.competitors.find(c => c.id === payload.id);
            competitor.points += payload.points;
        }
    },
    INCREMENT_MEMBER_SEASON_POINTS(payload) {
        if (this.guild !== null) {
            const found = this.guild.members.find(m => m.id === payload.user_id);
            if (found) {
                found.season_points += payload.points;
            }
        }
    },
    INCREMENT_MEMBER_VIP_POINTS(payload) {
        if (this.guild !== null) {
            const found = this.guild.members.find(m => m.id === payload.user_id);
            if (found) {
                found.vip_points += payload.points;
            }
        }
    },
    processLogin(payload) {
        return new Promise((resolve) => {
            StorageManager.set('identifier', payload.identifier);
            this.SET_CHARACTERS(payload.data.characters);
            this.SET_ABILITY_POINTS(payload.data.ability_points);
            this.SET_CHARACTER_POINTS(payload.data.character_points);
            this.SET_ATTRIBUTE_POINTS(payload.data.currencies.attribute_points);
            this.SET_COINS(payload.data.currencies.coins);
            this.SET_TICKETS(payload.data.currencies.tickets);
            this.SET_APPLICATIONS(payload.data.applications);
            this.SET_INVITES(payload.data.invites);
            this.SET_GUILD(payload.data.guild);
            this.SET_NOTIFICATION_PREFERENCES(payload.data.notification_preferences);
            this.SET_NOTIFICATIONS(payload.data.notifications);
            this.SET_ID(payload.data.user.id);
            this.SET_TOKEN(payload.data.user.token);
            this.SET_SESSION(payload.data.user.session);
            this.SET_USERNAME(payload.data.user.username);
            this.SET_COPPA(payload.data.user.coppa);
            this.SET_TUTORIAL_COMPLETED(payload.data.user.tutorial_completed || false);
            this.SET_PROFANITY(payload.data.user.profanity);
            this.SET_PASSCODE(payload.data.user.passcode);
            this.SET_UNCLAIMED_VIP(payload.data.user.unclaimed_vip || 0);
            this.lifetime_vip = payload.data.user.lifetime_vip || 0;
            this.win_streak = payload.data.user.win_streak || 0;
            this.subscription_active = payload.data.user.subscription_active || false;
            this.subscription_cancelled = payload.data.user.subscription_cancelled || false;
            this.subscription_expires_at = payload.data.user.subscription_expires_at || null;
            this.SET_STREAK(payload.data.streak);
            this.SET_LOGIN_REWARDS(payload.data.login_rewards);
            this.SET_RELEASE_NOTES(payload.data.release_notes);
            this.SET_SALES(payload.data.sales);
            this.SET_LIMITED_CHESTS(payload.data.limited_chests);
            this.SET_LIMITED_DISCOUNTS(payload.data.limited_discounts);
            this.SET_SEASON(payload.data.season);
            this.SET_ACHIEVEMENTS(payload.data.achievements || []);
            this.ads_enabled = payload.data.ads ? payload.data.ads.enabled : false;
            this.ads_rewarded_unit_id = payload.data.ads ? payload.data.ads.rewarded_unit_id : null;
            EchoManager.connect().catch(error => console.error('Echo connection failed', error));
            PushManager.initialize().catch(error => console.error('Push initialization failed', error));
            PushManager.consumePendingDeepLink();
            IapManager.initialize().catch(error => console.error('IAP initialization failed', error));
            AdManager.initialize().catch(error => console.error('AdManager initialization failed', error));
            cleanupStaleNotifications();
            resolve();
        });
    },
    registerRequest(payload) {
        return new Promise((resolve, reject) => {
            request('register', {
                method: 'post',
                data: payload,
            }, true)
                .then(({data}) => {
                    this.processLogin({identifier: payload.identifier, data}).then(() => {
                        resolve();
                    });
                })
                .catch(reject);
        });
    },
    connectRequest(payload) {
        return new Promise((resolve, reject) => {
            request('connect', {
                method: 'post',
                data: payload,
                timeout: 5000,
            }, true)
                .then(({data}) => {
                    this.processLogin({identifier: payload.identifier, data})
                        .then(() => {
                            resolve();
                        });
                })
                .catch(reject);
        });
    },
    masqueradeRequest(payload) {
        return new Promise((resolve, reject) => {
            request('masquerade', {
                method: 'post',
                data: payload,
            }, true)
                .then(({data}) => {
                    this.processLogin({identifier: payload.identifier, data})
                        .then(() => {
                            this.SET_MASQUERADE(true);
                            resolve();
                        });
                })
                .catch(reject);
        });
    },
    logoutAction() {
        return new Promise((resolve) => {
            EchoManager.onDisconnected(false);
            PushManager.unregister().catch(() => {});
            StorageManager.delete('identifier');
            StorageManager.delete('isMasquerade');
            StorageManager.delete('replayMode');
            this.RESET();
            this.isMasquerade = false;
            this.replayMode = false;
            resolve();
        });
    },
    suggestUsernameRequest() {
        return new Promise((resolve, reject) => {
            request('username')
                .then(({data}) => {
                    resolve(data.username);
                })
                .catch(reject);
        });
    },
    setCoppaRequest(coppa) {
        return new Promise((resolve, reject) => {
            request('coppa', {
                method: 'post',
                data: {coppa},
            })
                .then(({data}) => {
                    this.SET_COPPA(coppa);
                    if (data.login_rewards && data.login_rewards.length > 0) {
                        this.SET_LOGIN_REWARDS(data.login_rewards);
                        const allRewards = data.login_rewards.flatMap(group => group.rewards);
                        this.processRewards(allRewards);
                    }
                    resolve();
                })
                .catch(reject);
        });
    },
    starterCharactersRequest() {
        return new Promise((resolve, reject) => {
            request('starter-characters')
                .then(({data}) => {
                    resolve(data);
                })
                .catch(reject);
        });
    },
    selectStarterCharacterRequest(character_id) {
        return new Promise((resolve, reject) => {
            request('starter-character', {
                method: 'post',
                data: {character_id},
            })
                .then(({data}) => {
                    this.ADD_CHARACTER(data);
                    resolve();
                })
                .catch(reject);
        });
    },
    tutorialCompleteRequest() {
        return new Promise((resolve, reject) => {
            request('tutorial/complete', {
                method: 'post',
            })
                .then(({data}) => {
                    this.SET_TUTORIAL_COMPLETED(true);
                    // Update achievement progress in store
                    if (data.achievement) {
                        const existing = this.achievements.find(a => a.achievement_id === data.achievement.achievement_id);
                        if (existing) {
                            existing.progress = data.achievement.progress;
                            existing.tier_claimed = data.achievement.tier_claimed;
                        } else {
                            this.achievements.push({
                                achievement_id: data.achievement.achievement_id,
                                progress: data.achievement.progress,
                                tier_claimed: data.achievement.tier_claimed,
                            });
                        }
                    }
                    // Process tutorial rewards into currencies (without triggering the login rewards modal)
                    if (data.login_rewards && data.login_rewards.length > 0) {
                        const allRewards = data.login_rewards.flatMap(group => group.rewards);
                        this.processRewards(allRewards);
                    }
                    resolve(data);
                })
                .catch(reject);
        });
    },
    setProfanityRequest(profanity) {
        return new Promise((resolve, reject) => {
            request('profanity', {
                method: 'post',
                data: {profanity},
            })
                .then(() => {
                    this.SET_PROFANITY(profanity);
                    resolve();
                })
                .catch(reject);
        })
    },
    setPasscodeRequest(passcode) {
        return new Promise((resolve, reject) => {
            request('passcode', {
                method: 'post',
                data: {
                    passcode,
                },
            })
                .then(() => {
                    this.SET_PASSCODE(true);
                    resolve();
                })
                .catch(reject);
        })
    },
    removePasscodeRequest(passcode) {
        return new Promise((resolve, reject) => {
            request('passcode/remove', {
                method: 'post',
                data: {
                    passcode,
                },
            })
                .then(() => {
                    this.SET_PASSCODE(false);
                    resolve();
                })
                .catch(reject);
        })
    },
    // TODO: MOVE GUILD FUNCTIONS TO SEPARATE MODULE
    guildSearchRequest(data) {
        return new Promise((resolve, reject) => {
            request('guilds', {
                method: 'post',
                data
            })
                .then(response => {
                    resolve(response.data)
                })
                .catch(reject);
        });
    },
    guildDetailsRequest(guild_id) {
        return new Promise((resolve, reject) => {
            request('guilds/details/' + guild_id)
                .then(({data}) => {
                    resolve(data);
                })
                .catch(reject);
        });
    },
    guildCreateRequest(name) {
        return new Promise((resolve, reject) => {
            request('guild', {
                method: 'post',
                data: {
                    name
                }
            })
                .then(response => {
                    this.SET_GUILD(response.data);
                    this.UPDATE_COINS(-50);
                    if (this.unclaimed_vip > 0) {
                        this.INCREMENT_GUILD_VIP_POINTS(this.unclaimed_vip);
                        this.unclaimed_vip = 0;
                    }
                    resolve();
                })
                .catch(reject);
        });
    },
    guildRenameRequest(name) {
        return new Promise((resolve, reject) => {
            request('guild/rename', {
                method: 'post',
                data: {
                    name
                }
            })
                .then(() => {
                    this.RENAME_GUILD(name);
                    this.UPDATE_COINS(-50);
                    resolve();
                })
                .catch(reject);
        });
    },
    guildSetMotDRequest(motd) {
        return new Promise((resolve, reject) => {
            request('guild/motd', {
                method: 'post',
                data: {
                    motd,
                },
            })
                .then(() => {
                    this.SET_GUILD_MOTD(motd);
                    resolve();
                })
                .catch(reject);
        });
    },
    guildQuitRequest() {
        return new Promise((resolve, reject) => {
            request('guild/quit')
                .then(() => {
                    this.SET_GUILD(null);
                    resolve();
                })
                .catch(reject);
        });
    },
    guildDisbandRequest() {
        return new Promise((resolve, reject) => {
            request('guild/disband')
                .then(() => {
                    this.SET_GUILD(null);
                    resolve();
                })
                .catch(reject);
        });
    },
    guildChatRequest(body) {
        return new Promise((resolve, reject) => {
            request('guild/chat', {
                method: 'post',
                data: {
                    body
                }
            })
                .then(response => {
                    this.ADD_GUILD_CHAT_MESSAGE(Object.assign({}, response.data, {
                        user_id: this.id,
                        read_by: [this.id],
                        body,
                    }));
                    resolve();
                })
                .catch(reject);
        });
    },
    guildChatMessagesRequest() {
        return new Promise((resolve, reject) => {
            request('guild/chat', {
                params: {
                    cursor: this.guild.messages.cursor
                },
            })
                .then(response => {
                    response.data.data.forEach(message => {
                        this.ADD_GUILD_CHAT_MESSAGE(message);
                    });
                    this.SET_GUILD_CHAT_CURSOR(response.data.cursor);
                    resolve();
                })
                .catch(reject);
        });
    },
    guildChatMessagesReadRequest(lastReadId, count) {
        return new Promise((resolve, reject) => {
            request('guild/chat/read', {
                method: 'post',
                data: {
                    last_read_id: lastReadId,
                },
            })
                .then(() => {
                    this.guild.messages.data.forEach(message => {
                        if (! message.read && message.id <= lastReadId) {
                            message.read = true;
                        }
                    });
                    this.UPDATE_UNREAD_MESSAGES(-count);
                    resolve();
                })
                .catch(reject);
        });
    },
    guildLeaderRequest(user_id) {
        return new Promise((resolve, reject) => {
            request('guild/leader/' + user_id)
                .then(() => {
                    this.SET_GUILD_LEADER(user_id);
                    resolve();
                })
                .catch(reject);
        });
    },
    guildPromoteRequest(user_id) {
        return new Promise((resolve, reject) => {
            request('guild/promote/' + user_id)
                .then(() => {
                    this.PROMOTE_GUILD_MEMBER(user_id);
                    resolve();
                })
                .catch(e => {
                    reject(e);
                });
        });
    },
    guildDemoteRequest(user_id) {
        return new Promise((resolve, reject) => {
            request('guild/demote/' + user_id)
                .then(() => {
                    this.DEMOTE_GUILD_MEMBER(user_id);
                    resolve();
                })
                .catch(reject);
        });
    },
    guildKickRequest(user_id) {
        return new Promise((resolve, reject) => {
            request('guild/kick/' + user_id)
                .then(() => {
                    this.REMOVE_GUILD_MEMBER(user_id);
                    resolve();
                })
                .catch(reject);
        });
    },
    guildApplyRequest(guild_id) {
        return new Promise((resolve, reject) => {
            request('guild/apply/' + guild_id)
                .then(response => {
                    this.ADD_APPLICATION(response.data);
                    resolve();
                })
                .catch(reject);
        });
    },
    guildWithdrawApplicationRequest(guild_id) {
        return new Promise((resolve, reject) => {
            request('guild/applications/withdraw/' + guild_id)
                .then(() => {
                    this.REMOVE_APPLICATION(guild_id);
                    resolve();
                })
                .catch(reject);
        });
    },
    guildApproveApplicationRequest(user_id) {
        return new Promise((resolve, reject) => {
            request('guild/applications/approve/' + user_id)
                .then(({data}) => {
                    this.REMOVE_GUILD_APPLICATION(data.id);
                    this.ADD_GUILD_MEMBER(data);
                    cleanupStaleNotifications();
                    resolve();
                })
                .catch(reject);
        });
    },
    guildRejectApplicationRequest(user_id) {
        return new Promise((resolve, reject) => {
            request('guild/applications/reject/' + user_id)
                .then(() => {
                    this.REMOVE_GUILD_APPLICATION(user_id);
                    cleanupStaleNotifications();
                    resolve();
                })
                .catch(reject);
        });
    },
    guildInviteRequest(user_id) {
        return new Promise((resolve, reject) => {
            request('guild/invite/' + user_id)
                .then(response => {
                    this.ADD_GUILD_INVITE(response.data);
                    resolve();
                })
                .catch(reject);
        });
    },
    guildRevokeInviteRequest(user_id) {
        return new Promise((resolve, reject) => {
            request('guild/invites/revoke/' + user_id)
                .then(() => {
                    this.REMOVE_GUILD_INVITE(user_id);
                    cleanupStaleNotifications();
                    resolve();
                })
                .catch(reject);
        });
    },
    guildAcceptInviteRequest(guild_id) {
        return new Promise((resolve, reject) => {
            request('guild/invites/accept/' + guild_id)
                .then(response => {
                    this.REMOVE_INVITE(guild_id);
                    this.SET_GUILD(response.data);
                    cleanupStaleNotifications();
                    resolve();
                })
                .catch(reject);
        });
    },
    guildDeclineInviteRequest(guild_id) {
        return new Promise((resolve, reject) => {
            request('guild/invites/decline/' + guild_id)
                .then(() => {
                    this.REMOVE_INVITE(guild_id);
                    cleanupStaleNotifications();
                    resolve();
                })
                .catch(reject);
        });
    },
    guildRewardsRequest() {
        return new Promise((resolve, reject) => {
            request('guild/season-rewards')
                .then(() => {
                    this.processRewards(this.guild.rewards).then(() => {
                        this.SET_GUILD_REWARDS([]);
                        resolve();
                    });
                })
                .catch(reject);
        });
    },
    characterPurchaseRequest(character_id) {
        return new Promise((resolve, reject) => {
            request('characters/' + character_id)
                .then(response => {
                    const base_tier = this.references.data.design.character.find(c => c.id === character_id).base_tier;
                    const cost = this.tier_costs.filter(c => c.id <= base_tier).reduce((total, tier) => total + tier.cost, 0);
                    this.UPDATE_CHARACTER_POINTS({
                        id: character_id,
                        change: cost * -1,
                    })
                    this.ADD_CHARACTER(response.data);
                    resolve();
                })
                .catch(reject);
        });
    },
    characterTierRequest(character_id) {
        return new Promise((resolve, reject) => {
            request('characters/' + character_id + '/tier')
                .then(() => {
                    this.UPDATE_CHARACTER_POINTS({
                        id: character_id,
                        change: this.charactersDetailed.find(c => c.id === character_id).nextTierCost * -1
                    });
                    this.INCREMENT_CHARACTER_TIER(character_id);
                    resolve();
                })
                .catch(reject);
        });
    },
    characterAbilityPurchaseRequest(payload) {
        return new Promise((resolve, reject) => {
            request('characters/' + payload.character_id + '/abilities/' + payload.ability_id)
                .then(() => {
                    this.UPDATE_ABILITY_POINTS({
                        id: payload.ability_id,
                        change: this.ability_costs.find(c => c.id === 1).cost * -1,
                    });
                    this.ADD_CHARACTER_ABILITY(payload);
                    resolve();
                })
                .catch(reject);
        });
    },
    characterAbilityTierRequest(payload) {
        return new Promise((resolve, reject) => {
            request('characters/' + payload.character_id + '/abilities/' + payload.ability_id + '/tier')
                .then(() => {
                    const character = this.characters.find(c => c.id === payload.character_id);
                    if (character) {
                        const ability = character.abilities.find(a => a.id === payload.ability_id);
                        if (ability) {
                            const ability_cost = this.ability_costs.find(c => c.id === (ability.tier + 1));
                            if (ability_cost) {
                                this.UPDATE_ABILITY_POINTS({
                                    id: payload.ability_id,
                                    change: (ability_cost.cost * -1)
                                });
                                this.INCREMENT_CHARACTER_ABILITY_TIER(payload);
                            }
                        }
                    }
                    resolve();
                })
                .catch(reject);
        });
    },
    characterLoadoutUpdateRequest(payload) {
        return new Promise((resolve, reject) => {
            request('characters/' + payload.character_id + '/loadouts/' + payload.loadout_id, {
                method: 'post',
                data: {
                    slots: payload.slots,
                },
            })
                .then(() => {
                    this.SET_CHARACTER_LOADOUT(payload);
                    resolve();
                })
                .catch(reject);
        });
    },
    characterTreePurchaseRequest(character_id) {
        return new Promise((resolve, reject) => {
            request('characters/' + character_id + '/trees')
                .then(response => {
                    this.UPDATE_COINS(-500);   // TODO: SET TO A CONSTANT
                    this.ADD_CHARACTER_TREE({
                        character_id,
                        id: response.data.id,
                    });
                    resolve(response.data.id);
                })
                .catch(reject);
        });
    },
    characterLoadoutPurchaseRequest(character_id) {
        return new Promise((resolve, reject) => {
            request('characters/' + character_id + '/loadouts')
                .then(response => {
                    this.UPDATE_COINS(-500);   // TODO: SET TO A CONSTANT
                    this.ADD_CHARACTER_LOADOUT({
                        character_id,
                        id: response.data.id,
                    });
                    resolve(response.data.id);
                })
                .catch(reject);
        });
    },
    characterTreeUpdateRequest(payload) {
        return new Promise((resolve, reject) => {
            request('characters/' + payload.character_id + '/trees/' + payload.tree_id, {
                method: 'post',
                data: {
                    attribute_id: payload.attribute_id,
                    quantity: payload.quantity,
                },
            })
                .then(() => {
                    this.INCREMENT_TREE_ATTRIBUTE(payload);
                    this.UPDATE_ATTRIBUTE_POINTS((-1 * payload.quantity));
                    resolve();
                })
                .catch(reject);
        });
    },
    characterTreeResetRequest(payload) {
        return new Promise((resolve, reject) => {
            request('characters/' + payload.character_id + '/trees/' + payload.tree_id + '/reset')
                .then(() => {
                    this.UPDATE_COINS(-100);   // TODO: SET TO A CONSTANT
                    this.RESET_CHARACTER_TREE(payload);
                    resolve();
                })
                .catch(reject);
        });
    },
    readNotificationsRequest(notifications) {
        return new Promise((resolve, reject) => {
            request('notifications/read', {
                method: 'post',
                data: {notifications},
            })
                .then(() => {
                    notifications.forEach(notification => {
                        this.MARK_NOTIFICATION_READ(notification);
                    });
                    resolve();
                })
                .catch(reject);
        });
    },
    deleteNotificationRequest(notification_id) {
        return new Promise((resolve, reject) => {
            request('notifications/' + notification_id + '/delete')
                .then(() => {
                    this.REMOVE_NOTIFICATION(notification_id);
                    resolve();
                })
                .catch(reject);
        });
    },
    processRewards(rewards) {
        return new Promise((resolve) => {
            rewards.forEach(reward => {
                const reference = this.references.data.design.reward.find(r => r.id === reward.id);
                if (reference) {
                    switch (reference.reward_type_id) {
                        case RewardType.COINS:
                            this.UPDATE_COINS(reward.quantity);
                            break;
                        case RewardType.TICKETS:
                            this.UPDATE_TICKETS(reward.quantity);
                            break;
                        case RewardType.ATTRIBUTE_POINTS:
                            this.UPDATE_ATTRIBUTE_POINTS(reward.quantity);
                            break;
                        case RewardType.ABILITY_POINTS:
                            this.UPDATE_ABILITY_POINTS({id: reference.resource_id, change: reward.quantity});
                            break;
                        case RewardType.CHARACTER_POINTS:
                            this.UPDATE_CHARACTER_POINTS({id: reference.resource_id, change: reward.quantity});
                            break;
                        default:
                            //
                    }
                }
            });
            resolve();
        });
    },
    singlePlayerCompleteRequest(payload) {
        return new Promise((resolve, reject) => {
            request('bouts/single-player', {
                method: 'post',
                data: payload,
            })
                .then(({data}) => {
                    this.processRewards(data.rewards).then(() => {
                        resolve(data.rewards);
                    });
                })
                .catch(reject);
        });
    },
    startSinglePlayerSessionRequest(payload) {
        return new Promise((resolve, reject) => {
            request('bouts/single-player/session', {
                method: 'post',
                data: payload,
            })
                .then(({data}) => {
                    resolve(data.session_id);
                })
                .catch(reject);
        });
    },
    joinMatchmakingRequest(character_id) {
        return new Promise((resolve, reject) => {
            request('bouts/multi-player/join-matchmaking/' + character_id)
                .then(() => {
                    resolve();
                })
                .catch(reject);
        });
    },
    leaveMatchmakingRequest() {
        return new Promise((resolve, reject) => {
            // TODO: THIS SEEMS TO FIRE A leave-matchmaking REDIS PUBSUB EVENT ON PAGE REFRESH.  INVESTIGATE.
            request('bouts/multi-player/leave-matchmaking')
                .then(() => {
                    resolve();
                })
                .catch(reject);
        });
    },
    sendMatchInviteRequest(target_user_id, character_id) {
        return new Promise((resolve, reject) => {
            request('guild/match-invite/' + target_user_id, {
                method: 'post',
                data: {character_id},
            })
                .then(() => {
                    resolve();
                })
                .catch(reject);
        });
    },
    acceptMatchInviteRequest(inviter_id, character_id) {
        return new Promise((resolve, reject) => {
            request('guild/match-invite/' + inviter_id + '/accept', {
                method: 'post',
                data: {character_id},
            })
                .then(() => {
                    resolve();
                })
                .catch(reject);
        });
    },
    declineMatchInviteRequest(inviter_id) {
        return new Promise((resolve, reject) => {
            request('guild/match-invite/' + inviter_id + '/decline', {
                method: 'post',
            })
                .then(() => {
                    resolve();
                })
                .catch(reject);
        });
    },
    unlockDivisionRequest(payload) {
        return new Promise((resolve, reject) => {
            request('characters/' + payload.character + '/divisions/' + payload.division)
                .then(() => {
                    this.ADD_CHARACTER_DIVISION(payload);
                    resolve();
                })
                .catch(reject);
        });
    },
    purchaseChestRequest(payload) {
        const data = Object.prototype.hasOwnProperty.call(payload, 'passcode') && payload.passcode !== null && payload.passcode !== ''
            ? {passcode: payload.passcode}
            : {};
        // Store purchases carry the native proof; the backend derives the store
        // from the payment option's currency_type, so no platform field is sent.
        if (payload.purchase_token) {
            data.purchase_token = payload.purchase_token;
        }
        if (payload.receipt) {
            data.receipt = payload.receipt;
        }
        const options = {
            method: 'post',
            data,
        };
        // An idempotency key lets a retried fulfilment (e.g. the request
        // succeeds at the store but the app loses the response) resolve to the
        // same reward bundle instead of double-granting.
        if (payload.idempotency_key) {
            options.headers = {'X-Idempotency-Key': payload.idempotency_key};
        }
        return new Promise((resolve, reject) => {
            request('chests/' + payload.chest_id + '/' + payload.payment_option.id, options)
                .then(({data}) => {
                    if (payload.payment_option.currency_type === CurrencyType.COINS) {
                        this.UPDATE_COINS((payload.payment_option.amount * -1));
                    }
                    if (payload.max_purchases) {
                        this.UPDATE_LIMITED_CHEST(payload.chest_id);
                    }
                    if (payload.max_discount_purchases && payload.discount_id !== null) {
                        this.UPDATE_LIMITED_DISCOUNT(payload.discount_id)
                    }
                    if (payload.vip_points > 0) {
                        this.lifetime_vip = (this.lifetime_vip || 0) + payload.vip_points;
                        if (!this.guilded) {
                            this.unclaimed_vip = Math.min(100, (this.unclaimed_vip || 0) + payload.vip_points);
                        }
                    }
                    this.processRewards(data.rewards).then(() => {
                        EventBus.emit('chest-rewards', data.rewards);
                        resolve();
                    });
                })
                .catch(reject);
        });
    },
    /*
     * Achievements
     */
    SET_ACHIEVEMENTS(achievements) {
        this.achievements = achievements;
    },
    UPDATE_ACHIEVEMENT(achievement) {
        const index = this.achievements.findIndex(a => a.id === achievement.id);
        if (index !== -1) {
            this.achievements[index] = achievement;
        }
    },
    claimAchievementRequest(achievement_id) {
        return new Promise((resolve, reject) => {
            request('achievements/' + achievement_id + '/claim', {
                method: 'post',
            })
                .then(({data}) => {
                    const existing = this.achievements.find(a => a.achievement_id === achievement_id);
                    if (existing) {
                        existing.progress = data.progress;
                        existing.tier_claimed = data.tier_claimed;
                    } else {
                        this.achievements.push({
                            achievement_id: achievement_id,
                            progress: data.progress,
                            tier_claimed: data.tier_claimed,
                        });
                    }
                    if (data.rewards && data.rewards.length > 0) {
                        this.processRewards(data.rewards).then(() => {
                            resolve(data);
                        });
                    } else {
                        resolve(data);
                    }
                })
                .catch(reject);
        });
    },
};
